/**
 * dataStandardization.ts
 *
 * Client API service for Trung tâm Chuẩn hóa Dữ liệu (Data Standardization Hub).
 */
import { apiFetch } from '@/services/apiFetch'

export type DataScanType = 'MATERIALS' | 'QUOTES_VAT' | 'PRICE_RULE_MAPPING' | 'DUPLICATE_SPECS' | 'STANDARD_PACKAGING' | 'STRUCTURED_PACKAGING'
export type ConfidenceLevel = 'CONFIDENT' | 'NEEDS_REVIEW' | 'UNKNOWN'
export type DataAction = 'update' | 'archive' | 'deactivate' | 'delete' | 'create'

export interface DataProposalRow {
  id: string
  scan_type: DataScanType
  entity_type: string
  entity_id: string
  entity_code: string
  entity_name: string
  action: DataAction
  field: string
  old_value: any
  new_value: any
  current_display: string
  proposed_display: string
  confidence: ConfidenceLevel
  reason: string
  impact: string
  selected: boolean
  extra?: Record<string, any>
}

export interface DataScanStats {
  total_proposals: number
  confident_count: number
  needs_review_count: number
  unknown_count: number
  summary_info?: Record<string, any>
}

export interface DataScanResult {
  scan_type: DataScanType
  proposals: DataProposalRow[]
  stats: DataScanStats
}

export interface DataChangeBatch {
  id: string
  scan_type: string
  batch_name: string
  status: 'applied' | 'reverted' | 'partial_reverted'
  total_lines: number
  applied_lines: number
  reverted_lines: number
  created_by: string
  created_at: string
  reverted_by: string | null
  reverted_at: string | null
  notes: string | null
}

export interface DataChangeLine {
  id: string
  batch_id: string
  entity_type: string
  entity_id: string
  action: string
  field: string
  old_value: any
  new_value: any
  snapshot: any
  reverted: boolean
  revert_skipped_reason: string | null
  created_at: string
}

export interface HardwareImportRow {
  stt?: number
  supplier_code: string
  supplier_name: string
  item_name: string
  spec?: string
  color?: string
  uom: string
  unit_price: number
  vat_rate?: number
}

export interface HardwareImportPreviewItem {
  stt: number
  supplier_code: string
  supplier_name: string
  supplier_action: 'NEW' | 'EXISTING'
  item_name_raw: string
  item_name_standard: string
  is_name_standard: boolean
  spec: string
  color: string
  uom: string
  unit_price: number
  vat_rate: number
  material_action: 'CREATE_NEW' | 'REUSE_EXISTING'
  material_code: string
  material_id?: string
  price_rule_action: 'CREATE'
  valid: boolean
  error?: string
}

export interface HardwareImportPreviewResult {
  total_rows: number
  valid_rows: number
  invalid_rows: number
  new_suppliers_count: number
  new_materials_count: number
  reused_materials_count: number
  items: HardwareImportPreviewItem[]
}

async function fetchJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const res = await apiFetch(input, init)
  const data = await res.json()
  if (!res.ok) {
    const err = data as { error?: { message?: string } }
    throw new Error(err?.error?.message || `Lỗi HTTP ${res.status}`)
  }
  return data as T
}

export const dataStandardizationService = {
  /**
   * Quét dữ liệu theo danh mục: MATERIALS, QUOTES_VAT, PRICE_RULE_MAPPING
   */
  async scan(type: DataScanType): Promise<DataScanResult> {
    const res = await fetchJson<{ ok: boolean } & DataScanResult>(
      `/api/data-standardization/scan?type=${type}`,
    )
    return {
      scan_type: res.scan_type,
      proposals: res.proposals || [],
      stats: res.stats,
    }
  },

  /**
   * Duyệt và áp dụng các đề xuất đã chọn theo lô
   */
  async applyBatch(payload: {
    batch_name?: string
    scan_type: DataScanType
    proposals: DataProposalRow[]
  }): Promise<{
    batch: DataChangeBatch
    applied_count: number
    skipped_count: number
    skipped_items: any[]
  }> {
    const res = await fetchJson<{
      ok: boolean
      batch: DataChangeBatch
      applied_count: number
      skipped_count: number
      skipped_items: any[]
    }>('/api/data-standardization/apply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    return {
      batch: res.batch,
      applied_count: res.applied_count,
      skipped_count: res.skipped_count,
      skipped_items: res.skipped_items,
    }
  },

  /**
   * Hoàn tác toàn bộ 1 lô thay đổi
   */
  async undoBatch(batchId: string): Promise<{
    batch: DataChangeBatch
    reverted_count: number
    skipped_count: number
    skipped_items: any[]
  }> {
    const res = await fetchJson<{
      ok: boolean
      batch: DataChangeBatch
      reverted_count: number
      skipped_count: number
      skipped_items: any[]
    }>(`/api/data-standardization/undo/${batchId}`, {
      method: 'POST',
    })
    return {
      batch: res.batch,
      reverted_count: res.reverted_count,
      skipped_count: res.skipped_count,
      skipped_items: res.skipped_items,
    }
  },

  /**
   * Lấy danh sách các lô đã thực hiện
   */
  async getBatches(params?: { scanType?: string; status?: string }): Promise<DataChangeBatch[]> {
    const q = new URLSearchParams()
    if (params?.scanType) q.set('scan_type', params.scanType)
    if (params?.status) q.set('status', params.status)
    const res = await fetchJson<{ ok: boolean; batches: DataChangeBatch[] }>(
      `/api/data-standardization/batches${q.toString() ? `?${q.toString()}` : ''}`,
    )
    return res.batches || []
  },

  /**
   * Lấy chi tiết các dòng trong lô
   */
  async getBatchLines(batchId: string): Promise<DataChangeLine[]> {
    const res = await fetchJson<{ ok: boolean; lines: DataChangeLine[] }>(
      `/api/data-standardization/batches/${batchId}/lines`,
    )
    return res.lines || []
  },

  /**
   * Xem trước kiểm tra dữ liệu file Excel ngũ kim
   */
  async previewHardwareImport(rows: HardwareImportRow[]): Promise<HardwareImportPreviewResult> {
    const res = await fetchJson<{ ok: boolean } & HardwareImportPreviewResult>(
      '/api/data-standardization/import-hardware/preview',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows }),
      },
    )
    return {
      total_rows: res.total_rows,
      valid_rows: res.valid_rows,
      invalid_rows: res.invalid_rows,
      new_suppliers_count: res.new_suppliers_count,
      new_materials_count: res.new_materials_count,
      reused_materials_count: res.reused_materials_count,
      items: res.items || [],
    }
  },

  /**
   * Duyệt và áp dụng nạp danh mục ngũ kim
   */
  async applyHardwareImport(payload: {
    batch_name?: string
    rows: HardwareImportPreviewItem[]
  }): Promise<{
    batch: DataChangeBatch
    created_suppliers: number
    created_materials: number
    created_quotes: number
    created_price_rules: number
  }> {
    const res = await fetchJson<{
      ok: boolean
      batch: DataChangeBatch
      created_suppliers: number
      created_materials: number
      created_quotes: number
      created_price_rules: number
    }>('/api/data-standardization/import-hardware/apply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    return {
      batch: res.batch,
      created_suppliers: res.created_suppliers,
      created_materials: res.created_materials,
      created_quotes: res.created_quotes,
      created_price_rules: res.created_price_rules,
    }
  },
}
